import React, { FC, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Form, Input } from 'src/common';
import styles from '../index.module.scss';
import { isBoolean, isString, usePasswordValidator } from 'src/helpers';
import { SignUpFlow, useSignUpFlow } from 'src/models/auth';
import { isValidNumber } from 'libphonenumber-js';
import { FormInstance } from 'antd/lib/form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import classNames from 'classnames';
import { ReCaptchaV2 } from 'react-recaptcha-x';

type Props = { form: FormInstance; loading: boolean };
export const UserInfo: FC<Props> = ({ loading: isLoading, form }) => {
  const { t } = useTranslation();
  const passwordRule = usePasswordValidator();
  const [isRecaptchaChecked, setIsRecaptchaChecked] = useState<boolean>(() => form.getFieldValue('recaptcha'));
  const [step, setStep] = useSignUpFlow();
  const onPrev = useCallback(() => {
    setStep(SignUpFlow.CompanyInfo);
  }, [setStep]);

  const onRecaptchaChange = useCallback(
    (token: string | false | Error) => {
      if (isString(token)) {
        form.setFieldsValue({ recaptcha: true });
        setIsRecaptchaChecked(true);
      } else if (isBoolean(token) && !token) {
        form.setFieldsValue({ recaptcha: false });
        setIsRecaptchaChecked(false);
      } else if (token instanceof Error) {
        form.setFields([
          {
            name: 'recaptcha',
            errors: [token.message]
          }
        ]);
      }
    },
    [form]
  );

  return (
    <div className={classNames(styles.step, { [styles.isActive]: step === SignUpFlow.UserInfo })}>
      <div className={styles.heading}>
        <h3 className={styles.title}>
          <span>{t('Auth.signUp.title')}</span>
        </h3>
        <span className={styles.desc}>{t('Auth.signUp.desc')}</span>
      </div>
      <Form.Item
        name={'name'}
        label={t('Auth.signUp.labels.name')}
        rules={[{ required: true, message: t('Common.inputRequired', { inputName: t('Auth.signUp.labels.name') }) }]}
        className={styles.half}
      >
        <Input placeholder={t('Auth.signUp.placeholders.name')} autoComplete={'given-name'} type={'text'} />
      </Form.Item>
      <Form.Item
        name={'lastName'}
        label={t('Auth.signUp.labels.lastName')}
        rules={[
          { required: true, message: t('Common.inputRequired', { inputName: t('Auth.signUp.labels.lastName') }) }
        ]}
        className={styles.half}
      >
        <Input placeholder={t('Auth.signUp.placeholders.lastName')} autoComplete={'family-name'} type={'text'} />
      </Form.Item>
      <Form.Item
        name={'email'}
        label={t('Auth.emailAddress')}
        rules={[{ required: true, message: t('Auth.enterEmail') }]}
      >
        <Input placeholder={t('Auth.emailAddress')} autoComplete={'username'} type={'email'} />
      </Form.Item>
      <Form.Item
        name={'phone'}
        label={t('Auth.signUp.labels.phone')}
        rules={[
          { required: true, message: t('Common.inputRequired', { inputName: t('Auth.signUp.labels.phone') }) },
          ({ getFieldValue }) => ({
            validator(rule, value) {
              const country = getFieldValue('country');
              const isValid = isValidNumber(value, country);
              if (country && isValid) {
                return Promise.resolve();
              }
              return Promise.reject(t('Auth.signUp.errors.invalidPhoneNumber'));
            }
          })
        ]}
      >
        <Input placeholder={t('Auth.signUp.placeholders.phone')} type={'tel'} autoComplete={'tel'} />
      </Form.Item>
      <Form.Item
        name={'password'}
        label={t('Common.password')}
        rules={[
          { required: true, message: t('Auth.enterPassword') },
          { validator: passwordRule, message: t('Auth.passwordPolicy') }
        ]}
      >
        <Input.Password
          placeholder={t('Common.password')}
          autoComplete={'new-password'}
          type={'password'}
          iconRender={visible => <FontAwesomeIcon icon={visible ? faEye : faEyeSlash} />}
        />
      </Form.Item>
      <Form.Item name={'recaptcha'}>
        <ReCaptchaV2 callback={onRecaptchaChange} />
      </Form.Item>
      <div className={styles.actions}>
        <Button onClick={onPrev} type={'link'} className={styles.link}>
          {t('Common.back')}
        </Button>
        <Button type={'primary'} loading={isLoading} htmlType={'submit'} disabled={!isRecaptchaChecked}>
          {t('Auth.signUp.buttons.submit')}
        </Button>
      </div>
    </div>
  );
};

export default UserInfo;
